/**
 * Moderation Work Queue Page
 *
 * Displays assigned moderation cases for review in a multi-card layout.
 * Cards use a wrapping Flexbox grid that flows vertically (multiple rows)
 * so the page scrolls vertically rather than overflowing horizontally.
 * The labels panel operates on the focused (selected) card.
 */

import { ConversationContextModal } from '@content-search/components/ConversationContextModal';
import { ErrorBanner, AudioPlayerProvider } from '@core/components';
import type { DomainError } from '@core/domain';
import { useSystemConfig } from '@core/hooks/useSystemConfig';
import { srOnly } from '@core/styles/utilities.css';
import { ModerationActionMenu } from '@moderation/components/ModerationCaseCard';
import { ModerationClassificationDialog } from '@moderation/components/ModerationClassificationDialog';
import { ModerationLabelsPanel } from '@moderation/components/ModerationLabelsPanel';
import { useBodyScrollLock } from '@moderation/hooks/useBodyScrollLock';
import { useModerationWorkQueueSetup } from '@moderation/hooks/useModerationWorkQueueSetup';
import { Card, Heading } from '@soteria/design-system-ve';
import type { ReactElement } from 'react';

import * as styles from './ModerationWorkQueuePage.css';
import { ActionBar } from './components/ActionBar';
import { CaseCardRenderer } from './components/CaseCardRenderer';
import { ExpiryWarningDialog } from './components/ExpiryWarningDialog';
import { CompletedState, EmptyQueueState, ErrorState, LoadingState } from './components/ModerationWorkQueueStates';
import { SessionExpiredDialog } from './components/SessionExpiredDialog';
import { UnsavedCasesDialog } from './components/UnsavedCasesDialog';

/** Render inline error banners when present */
function renderErrorBanner(errors: DomainError[] | null, title: string): ReactElement | null {
  if (!errors || errors.length === 0) return null;

  return <ErrorBanner errors={errors} title={title} />;
}

/** Build the sr-only status announcement text */
function getStatusAnnouncement(count: number): string {
  if (count === 0) return '';

  return `${count} ${count === 1 ? 'case has' : 'cases have'} labels applied`;
}

export function ModerationWorkQueuePage(): ReactElement | null {
  const q = useModerationWorkQueueSetup();
  const { mediaServiceUrl } = useSystemConfig();

  // Lock body & html scroll — this page manages its own scroll region (card grid)
  useBodyScrollLock();

  if (q.loading) return <LoadingState />;
  if (q.errors && q.errors.length > 0) return <ErrorState message={q.errors[0].message} onReturn={q.handleReturnToDashboard} />;
  if (q.isEmpty) return <EmptyQueueState hasActiveFilter={q.hasActiveFilter} onReturn={q.handleReturnToDashboard} />;
  if (q.isCompleted) return <CompletedState totalCases={q.completedCount} onReturn={q.handleReturnToDashboard} />;

  return (
    <AudioPlayerProvider>
      <div className={styles.container}>
        <Heading className={srOnly} level="h1">
          {q.topicName} Moderation Queue
        </Heading>

        {renderErrorBanner(q.submitErrors, 'Failed to submit cases:')}

        <Card className={styles.actionBarCard} elevation="none" padding="medium">
          <ActionBar
            appliedCount={q.appliedCount}
            formattedTime={q.countdown.formattedTime}
            handleStopModeration={q.handleStopModeration}
            handleSubmit={q.handleSubmit}
            handleSubmitAndStop={q.handleSubmitAndStop}
            isExpiringSoon={q.countdown.isExpiringSoon}
            isSubmitDisabled={q.isSubmitDisabled}
            pendingCount={q.pendingCount}
            previewOptions={q.previewOptions}
            submitLoading={q.submitLoading}
            onTogglePreview={q.togglePreview}
          />
        </Card>

        <div className={styles.contentGrid}>
          {/* Scrollable card grid column — keeps the action bar sticky above */}
          <div className={styles.cardGridScrollContainer}>
            {/* Keyboard navigation wrapper — role="listbox" for arrow-key roving tabindex */}
            <div
              ref={q.cardGridRef}
              aria-label={`${q.totalCases} moderation ${q.totalCases === 1 ? 'case' : 'cases'}`}
              aria-orientation="horizontal"
              role="listbox"
              tabIndex={-1}
              onKeyDown={q.handleCardGridKeyDown}
            >
              <div className={styles.cardGrid}>
                {q.sessionCases.map(moderationCase => {
                  const isFocused = moderationCase.caseId === q.focusedCaseId;

                  return (
                    <CaseCardRenderer
                      key={moderationCase.caseId}
                      actionMenuTriggerId={isFocused ? q.classificationMenu.actionMenuTriggerId : undefined}
                      cardState={q.cardStates[moderationCase.caseId]}
                      isFocused={isFocused}
                      labels={q.communityLabels}
                      moderationCase={moderationCase}
                      previewOptions={q.previewOptions}
                      showActionMenu={isFocused}
                      onActionMenuToggle={isFocused ? q.classificationMenu.handleActionMenuToggle : undefined}
                      onFocus={q.focusCase}
                      onUndo={q.undoLabels}
                    />
                  );
                })}
              </div>
            </div>
          </div>

          {q.focusedCase && (
            <div className={styles.labelsColumn}>
              <ModerationLabelsPanel
                key={q.focusedCase.caseId}
                caseId={q.focusedCase.caseId}
                comment={q.comment}
                communityId={q.communityId}
                errors={q.submitErrors}
                isSubmitting={q.submitLoading}
                selectedLabels={q.selectedLabels}
                onApply={q.handleApplyLabels}
                onCommentChange={q.setComment}
                onSelectionChange={q.setSelectedLabels}
              />
            </div>
          )}

          {/* Action Menu (anchored to focused card's "..." trigger button) */}
          <ModerationActionMenu
            isOpen={q.classificationMenu.isActionMenuOpen}
            triggerId={q.classificationMenu.actionMenuTriggerId}
            onClassification={q.classificationMenu.handleClassification}
            onClose={q.classificationMenu.handleActionMenuClose}
            onContext={q.conversationContextMenu?.handleOpenContextModal}
          />

          {/* Classification Result Dialog (loading → no data → data) */}
          <ModerationClassificationDialog
            classificationDialogData={q.classificationMenu.classificationDialogData}
            errors={q.classificationMenu.classificationErrors}
            isContentBlurred={q.classificationMenu.isContentBlurred}
            isOpen={q.classificationMenu.isClassificationDialogOpen}
            isSearching={q.classificationMenu.isClassificationSearching}
            searchCompleted={q.classificationMenu.classificationSearchCompleted}
            onClose={q.classificationMenu.closeClassificationDialog}
            onToggleBlur={q.classificationMenu.toggleClassificationBlur}
          />

          {/* Conversation Context Modal (feature-flagged) */}
          {q.conversationContextMenu && (
            <ConversationContextModal
              communityId={q.conversationContextMenu.contextModalProps.communityId}
              contentSearchErrors={q.conversationContextMenu.contextModalProps.contentSearchErrors}
              isOpen={q.conversationContextMenu.isContextModalOpen}
              isSearchingContent={q.conversationContextMenu.contextModalProps.isSearchingContent}
              mediaServiceUrl={mediaServiceUrl}
              metadata={q.conversationContextMenu.contextModalProps.metadata}
              selectedMessage={q.conversationContextMenu.contextModalProps.selectedMessage}
              subjectId={q.conversationContextMenu.contextModalProps.subjectId}
              topicName={q.conversationContextMenu.contextModalProps.topicName}
              onClose={q.conversationContextMenu.handleCloseContextModal}
            />
          )}

          <div aria-live="polite" className={srOnly} role="status">
            {getStatusAnnouncement(q.appliedCount)}
          </div>
        </div>

        <UnsavedCasesDialog blocker={q.blocker} />
        <ExpiryWarningDialog isOpen={q.countdown.showWarning} onDismiss={q.countdown.dismissWarning} />
        <SessionExpiredDialog
          isOpen={q.countdown.showExpired}
          onReturn={() => {
            q.countdown.dismissExpired();
            q.handleStopModeration();
          }}
        />
      </div>
    </AudioPlayerProvider>
  );
}
